import { RECEIVE_CONFIRM_SCOPE, type ReceiveConfirmScope, type ReceiveTableRow } from '@/entities/receive';

/**
 * Unit the operator types quantities in. The API only ever accepts inventory
 * units (LTU counts); handling units are an entry convenience converted on the
 * way out via `ltusPerBundle`.
 */
export type ConfirmArrivalUnitMode = 'inventory' | 'handling';

/** Per-line quantities, always held in **inventory units** regardless of mode. */
export interface ConfirmArrivalLineValues {
  lineId: string;
  /** Sent back as given by the context — see `getConfirmLineKey`. */
  shipmentId?: string;
  goodQty: number;
  damagedQty: number;
  lostQty: number;
}

/** The three condition buckets the operator splits a line between. */
export type ConfirmArrivalQuantityField = 'goodQty' | 'damagedQty' | 'lostQty';

export interface ConfirmArrivalFormValues {
  targetLocationId: string;
  /** Storage-area seqno; `null` when the location has none or none is picked. */
  targetLocationSpaceSeq: number | null;
  /** `yyyy-MM-dd` — kept as a string so it round-trips to the API unchanged. */
  actualDateOfReceipt: string;
  comments: string;
  transportDocNumber: string;
  explanationOfLosses: string;
  printGoodsReceivedNote: boolean;
  lines: ConfirmArrivalLineValues[];
}

/**
 * The consignment row the modal was opened from. Only `shipmentId` is required
 * to talk to the backend — the rest is fallback header text for the moment
 * before the context call resolves.
 *
 * With `scope: 'WAYBILL'` the row is only the anchor: the backend widens the
 * receipt to every consignment of its waybill on the same truck.
 */
export interface ConfirmArrivalTarget {
  shipmentId: number;
  scope?: ReceiveConfirmScope;
  consignmentNumber?: string;
  waybill?: string;
  truckNumber?: string | null;
}

/**
 * Identity of a form line. `lineId` alone is not unique in waybill scope — a
 * consignment dispatched in two parts onto one truck under one waybill has the
 * same line in two shipments — so the shipment joins it when the context has one.
 */
export const getConfirmLineKey = (line: { lineId: string; shipmentId?: string }): string =>
  line.shipmentId ? `${line.shipmentId}:${line.lineId}` : line.lineId;

/** Narrows a dashboard row to the fields the modal needs. */
export const toConfirmArrivalTarget = (row: ReceiveTableRow): ConfirmArrivalTarget | null =>
  row.shipmentId == null
    ? null
    : {
        shipmentId: row.shipmentId,
        consignmentNumber: row.consignmentNumber,
        waybill: row.waybill,
        truckNumber: row.truckNumber
      };

/** A consignment row as the anchor of its waybill — `null` when there is no waybill to receive by. */
export const toConfirmWaybillTarget = (row: ReceiveTableRow): ConfirmArrivalTarget | null => {
  const target = toConfirmArrivalTarget(row);
  return target && row.waybill ? { ...target, scope: RECEIVE_CONFIRM_SCOPE.WAYBILL } : null;
};
